import React, { useRef, useImperativeHandle } from 'react';

import { Link as RouterLink } from 'react-router-dom';
import { Props } from './Link.d';
import styles from './Link.module.scss';
import { Icon, IconList } from 'components/core';
import { useReactRouterLink, convertUrlToPath } from 'components/core/router';

const Link = (
  {
    to,
    variant,
    onClick,
    component,
    className,
    htmlType,
    external = false,
    children,
    ...props
  }: Props,
  ref: React.ForwardedRef<any>
) => {
  const shouldUseRouterLink = useReactRouterLink(to);
  const path = convertUrlToPath(to);

  let componentType = component;
  if (!componentType)
    componentType = to ? (shouldUseRouterLink ? RouterLink : 'a') : 'button';

  const classes = [styles.Link];
  if (!to) classes.push(styles.LinkButton);
  if (variant) classes.push(styles[variant]);
  if (className) classes.push(className);

  const forwardedRef = useRef<any>(null);
  useImperativeHandle(ref, () => forwardedRef.current);

  // Plain function components (allowed by the `component` prop's type)
  // can't accept a ref, only strings and forwardRef components (typeof
  // 'object', unlike a plain function) can.
  const canReceiveRef = typeof componentType !== 'function';

  return React.createElement<any>(
    componentType,
    {
      ...(canReceiveRef ? { ref: forwardedRef } : {}),
      to: path,
      href: path,
      className: classes.join(' ') || '',
      onClick,
      type: htmlType,
      target: external ? '_blank' : undefined,
      rel: external ? 'noopener noreferrer' : undefined,
      ...props,
    },
    children,
    external ? (
      <Icon
        icon={IconList.light.faArrowUpRightFromSquare}
        aria-label="- opens in new window"
        aria-hidden={false}
      />
    ) : (
      ''
    )
  );
};

const forwardedLink = React.forwardRef<any, Props>(Link) as React.FC<Props>;

export { forwardedLink as Link };
